import React, { useMemo, useRef } from 'react';
import styled from 'styled-components';

const TimelineContainer = styled.div`
  position: fixed;
  bottom: 80px;
  left: 50%;
  transform: translateX(-50%);
  width: calc(100vw - 100px);
  max-width: 1400px;
  height: 60px;
  background: rgba(0, 0, 0, 0.95);
  border: 2px solid #00ffff;
  border-radius: 8px;
  z-index: 800;
  display: flex;
  align-items: center;
  padding: 0 50px;
  position: relative;
  backdrop-filter: blur(10px);
`;

const ScrollButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(0, 255, 255, 0.3);
  border: 2px solid #00ffff;
  color: #00ffff;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  cursor: pointer;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  font-weight: bold;
  transition: all 0.2s;
  user-select: none;

  &:hover {
    background: #00ffff;
    color: #000000;
    transform: translateY(-50%) scale(1.15);
  }

  &:active {
    transform: translateY(-50%) scale(0.95);
  }

  &.left {
    left: 8px;
  }

  &.right {
    right: 8px;
  }
`;

const ScrollWrapper = styled.div`
  width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  scroll-behavior: smooth;

  &::-webkit-scrollbar {
    display: none;
  }

  -ms-overflow-style: none;
  scrollbar-width: none;
`;

const TimelineTrack = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: max-content;
  padding: 0 10px;
`;

const TimelineNode = styled.div`
  min-width: 100px;
  height: 40px;
  background: ${props => props.$color ? `${props.$color}22` : 'rgba(0, 255, 255, 0.1)'};
  border: 2px solid ${props => props.$color || '#00ffff'};
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s;
  padding: 4px 8px;
  position: relative;

  &:hover {
    background: ${props => props.$color || '#00ffff'};
    transform: translateY(-4px) scale(1.05);
    box-shadow: 0 8px 16px ${props => props.$color || '#00ffff'}66;
  }

  &:hover span {
    color: #000000;
  }
`;

const NodeDate = styled.span`
  color: ${props => props.$color || '#00ffff'};
  font-size: 11px;
  font-weight: 700;
  transition: color 0.2s;
  text-transform: uppercase;
`;

const NodeLabel = styled.span`
  color: ${props => props.$color || '#00ffff'};
  font-size: 9px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 96px;
  transition: color 0.2s;
  font-weight: 500;
`;

const Connector = styled.div`
  width: 24px;
  height: 2px;
  background: linear-gradient(90deg, 
    ${props => props.$fromColor || '#00ffff'} 0%, 
    ${props => props.$toColor || '#00ffff'} 100%
  );
  opacity: 0.6;
`;

const Timeline = ({ nodes, onNodeSelect }) => {
  const scrollRef = useRef(null);

  const sortedNodes = useMemo(() => {
    if (!nodes || nodes.length === 0) return [];
    return [...nodes].sort((a, b) => new Date(a.date) - new Date(b.date));
  }, [nodes]);

  const scroll = (direction) => {
    if (!scrollRef.current) return;
    
    const scrollAmount = 400;
    const currentScroll = scrollRef.current.scrollLeft;
    
    scrollRef.current.scrollTo({
      left: direction === 'left' 
        ? currentScroll - scrollAmount 
        : currentScroll + scrollAmount,
      behavior: 'smooth'
    });
  };

  if (sortedNodes.length === 0) {
    return null;
  }

  return (
    <TimelineContainer>
      <ScrollButton 
        className="left" 
        onClick={() => scroll('left')}
        aria-label="Scroll left"
      >
        ‹
      </ScrollButton>
      
      <ScrollWrapper ref={scrollRef}>
        <TimelineTrack>
          {sortedNodes.map((node, index) => (
            <React.Fragment key={node.id}>
              <TimelineNode
                $color={node.color}
                onClick={() => onNodeSelect(node)}
                title={node.label}
              >
                <NodeDate $color={node.color}>
                  {new Date(node.date).toLocaleDateString('en-US', { 
                    month: 'short', 
                    day: 'numeric' 
                  })}
                </NodeDate>
                <NodeLabel $color={node.color}>
                  {node.label.substring(0, 15)}...
                </NodeLabel>
              </TimelineNode>
              {index < sortedNodes.length - 1 && (
                <Connector
                  $fromColor={node.color}
                  $toColor={sortedNodes[index + 1].color}
                />
              )}
            </React.Fragment>
          ))}
        </TimelineTrack>
      </ScrollWrapper>

      <ScrollButton 
        className="right" 
        onClick={() => scroll('right')}
        aria-label="Scroll right"
      >
        ›
      </ScrollButton>
    </TimelineContainer>
  );
};

export default Timeline;